import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { MapPin, Building2, Bath, BedDouble, Square, CalendarDays, Heart, Castle, HardHat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/toast/use-toast';

const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

const PropertyTag = ({ children, type = "default" }) => {
  // Colores diferentes según el tipo
  const colors = {
    default: "from-orange-500/90 to-amber-500/90",
    construccion: "from-blue-500/90 to-sky-500/90",
    terminado: "from-emerald-500/90 to-green-500/90",
    planos: "from-purple-500/90 to-indigo-500/90",
    lanzamiento: "from-orange-500/90 to-amber-500/90"
  };

  return (
    <div className="absolute top-4 left-4 overflow-hidden z-10">
      <div className={`px-3 py-0.5 text-xs font-medium 
                    bg-gradient-to-r ${colors[type]} 
                    text-white shadow-lg backdrop-blur-sm
                    transform transition-all duration-300 hover:scale-105 hover:shadow-xl
                    rounded-lg`}>
        {children}
      </div>
    </div>
  );
};

const FavoriteButton = ({ isFavorite, isLoading, onClick }) => (
  <button 
    className={`absolute top-4 right-4 p-2 rounded-full
              transform transition-all duration-300
              ${isLoading 
                ? 'bg-gray-200 cursor-not-allowed' 
                : isFavorite 
                  ? 'bg-red-50 hover:bg-red-100' 
                  : 'bg-white/90 hover:bg-white'
              } 
              backdrop-blur-sm shadow-lg
              hover:scale-110 hover:shadow-xl active:scale-95
              group/fav z-10`}
    onClick={onClick}
    disabled={isLoading}
  >
    <Heart 
      className={`w-4 h-4 transition-all duration-300
                ${isFavorite 
                  ? 'text-red-500 fill-current' 
                  : 'text-gray-500 group-hover/fav:text-red-400'}`}
    />
  </button>
);

const PropertyFeature = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1.5 text-gray-600 group/feature">
    <Icon className="w-3.5 h-3.5 transition-transform duration-300 group-hover/feature:scale-110" />
    <span className="text-xs transition-colors duration-300 group-hover/feature:text-gray-900">{children}</span>
  </div>
);

const PropertyImage = React.memo(({ url, alt, className }) => {
  const [error, setError] = useState(false);

  const getFullUrl = (imageUrl) => {
    if (!imageUrl) return '/default-property-image.jpg';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
  };

  const handleError = (e) => {
    console.error('Error loading image:', url);
    setError(true);
    e.target.src = '/default-property-image.jpg';
  };

  if (!url) {
    return (
      <img
        src="/default-property-image.jpg"
        alt={alt}
        className={className}
      />
    );
  }

  return (
    <img
      src={error ? '/default-property-image.jpg' : getFullUrl(url)}
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
});

PropertyImage.displayName = 'PropertyImage';

const PropertyCardPro = ({ 
  item = {
    id: 1,
    titulo: "Proyecto De Prueba",
    ubicacion: "MIRAFLORES",
    tipo: "DEPARTAMENTOS",
    area_total: "40",
    habitaciones: "1-2",
    fecha_lanzamiento: "2024-02",
    precio_soles: 367266,
    imagenes: [],
    isFavorite: false
  },
  isProject = false,
  onFavoriteUpdate 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFavorite, setIsFavorite] = useState(item.isFavorite || false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  // Adaptar a la estructura del item que puede ser propiedad o proyecto
  const property = isProject ? {
    id: item.id,
    titulo: item.nombre_proyecto || "Proyecto sin nombre",
    ubicacion: item.ubicacion || "Ubicación no especificada",
    tipo: item.tipo_proyecto || "Proyecto",
    area_total: getMinArea(),
    habitaciones: getDormitorios(),
    fecha_lanzamiento: item.fecha_publicacion,
    precio_soles: item.precio_desde || 0,
    imagenes: item.imagenes || [],
    isFavorite: item.isFavorite || false,
    estado_proyecto: item.estado_proyecto || "lanzamiento",
    mes_entrega: item.mes_entrega,
    anio_entrega: item.anio_entrega,
    total_unidades: item.total_unidades
  } : item;

  // Función para obtener el área mínima del proyecto
  function getMinArea() {
    if (!isProject) return item.area_total;
    
    const areas = [];
    if (item.tiene_1dorm && item.area_1dorm) areas.push(item.area_1dorm);
    if (item.tiene_2dorm && item.area_2dorm) areas.push(item.area_2dorm);
    if (item.tiene_3dorm && item.area_3dorm) areas.push(item.area_3dorm);
    if (item.tiene_4dorm && item.area_4dorm) areas.push(item.area_4dorm);
    
    if (areas.length === 0) return "Consultar";
    return Math.min(...areas);
  }

  // Función para obtener los dormitorios del proyecto
  function getDormitorios() {
    if (!isProject) return item.habitaciones;
    
    const dormitorios = [];
    if (item.tiene_1dorm) dormitorios.push("1");
    if (item.tiene_2dorm) dormitorios.push("2");
    if (item.tiene_3dorm) dormitorios.push("3");
    if (item.tiene_4dorm) dormitorios.push("4+");
    
    if (dormitorios.length === 0) return "Consultar";
    return dormitorios.join("-");
  }

  // Formatear la fecha de entrega para proyectos
  const getEntregaText = () => {
    if (!isProject) return "Próximamente";
    if (property.mes_entrega && property.anio_entrega) {
      return `${property.mes_entrega.charAt(0).toUpperCase() + property.mes_entrega.slice(1)} ${property.anio_entrega}`;
    }
    return "Consultar fecha";
  };

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const apiUrl = isProject 
          ? `https://backend-production-1786.up.railway.app/api/favoritos/check/${property.id}?tipo=proyecto` 
          : `https://backend-production-1786.up.railway.app/api/favoritos/check/${property.id}`;

        const response = await fetch(apiUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const { isFavorite: favoriteStatus } = await response.json();
          setIsFavorite(favoriteStatus);
        }
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    setIsFavorite(property.isFavorite || false);
    checkFavoriteStatus();
  }, [property.id, property.isFavorite, user, isProject]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: "Inicia sesión para guardar favoritos",
        description: "Necesitas una cuenta para guardar propiedades en favoritos",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No hay token disponible');

      const apiUrl = isProject 
        ? `https://backend-production-1786.up.railway.app/api/favoritos/${property.id}?tipo=proyecto` 
        : `https://backend-production-1786.up.railway.app/api/favoritos/${property.id}`;

      const response = await fetch(apiUrl, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al actualizar favoritos');

      const newFavoriteStatus = !isFavorite;
      setIsFavorite(newFavoriteStatus);
      
      toast({
        title: newFavoriteStatus ? "Añadido a favoritos" : "Eliminado de favoritos",
        description: newFavoriteStatus 
          ? `${isProject ? "El proyecto" : "La propiedad"} se añadió a tus favoritos` 
          : `${isProject ? "El proyecto" : "La propiedad"} se eliminó de tus favoritos`,
      });

      if (onFavoriteUpdate) {
        onFavoriteUpdate(property.id, newFavoriteStatus);
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar favoritos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const mainImage = Array.isArray(property.imagenes) && property.imagenes.length > 0 
    ? property.imagenes[0] 
    : null;

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
  };

  // Determinar el tag y color según el estado del proyecto o si es una propiedad normal
  const getTag = () => {
    if (!isProject) return <PropertyTag>LANZAMIENTO</PropertyTag>;
    
    switch(property.estado_proyecto?.toLowerCase()) {
      case 'planos':
        return <PropertyTag type="planos">EN PLANOS</PropertyTag>;
      case 'lanzamiento':
        return <PropertyTag type="lanzamiento">LANZAMIENTO</PropertyTag>;
      case 'construccion':
        return <PropertyTag type="construccion">EN CONSTRUCCIÓN</PropertyTag>;
      case 'terminado':
        return <PropertyTag type="terminado">TERMINADO</PropertyTag>;
      default:
        return <PropertyTag>PROYECTO</PropertyTag>;
    }
  };

  // Icono para mostrar según el estado del proyecto
  const getStatusIcon = () => {
    if (!isProject) return <CalendarDays className="w-3.5 h-3.5" />;
    
    switch(property.estado_proyecto?.toLowerCase()) {
      case 'planos':
        return <Castle className="w-3.5 h-3.5" />;
      case 'construccion':
        return <HardHat className="w-3.5 h-3.5" />;
      default:
        return <CalendarDays className="w-3.5 h-3.5" />;
    }
  };

  return (
    <Card 
      className="group relative overflow-hidden cursor-pointer bg-white
                w-full max-w-4xl mx-auto
                transform transition-all duration-500 ease-out
                hover:shadow-xl hover:scale-[1.02] active:scale-[0.99]
                border border-gray-200 hover:border-orange-200"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate(isProject ? `/proyecto/${property.id}` : `/propiedad/${property.id}`)}
    >
      <div className="flex flex-col md:flex-row h-auto md:h-56">
        {/* Imagen */}
        <div className="relative w-full md:w-56 h-48 md:h-full overflow-hidden">
          <PropertyImage
            url={mainImage}
            alt={property.titulo}
            className={`w-full h-full object-cover 
                     transition-all duration-700 ease-out
                     ${isHovered ? 'scale-110 blur-[1px]' : 'scale-100'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          
          {getTag()}
          <FavoriteButton 
            isFavorite={isFavorite}
            isLoading={isLoading}
            onClick={handleFavoriteClick}
          />
        </div>

        {/* Contenido */}
        <div className="flex-1 p-4 flex flex-col justify-between relative overflow-hidden">
          {/* Header */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 group-hover:translate-x-1 transition-transform duration-300">
              {getStatusIcon()}
              <span>{isProject ? getEntregaText() : "Febrero 2024"}</span>
            </div>

            <div className="transform transition-all duration-300 group-hover:translate-x-1">
              <h2 className="text-lg font-bold text-gray-900 group-hover:text-orange-600 transition-colors"
                  title={property.titulo}>
                {truncateText(property.titulo, 27)}
              </h2>
              <div className="flex items-center gap-1.5 mt-0.5 text-gray-500">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-xs">{truncateText(property.ubicacion, 35)}</span>
              </div>
            </div>

            {/* Características */}
            <div className="grid grid-cols-2 gap-3 transform transition-all duration-300 group-hover:translate-x-1">
              <PropertyFeature icon={Building2}>{property.tipo}</PropertyFeature>
              <PropertyFeature icon={Square}>
                {isProject ? `Desde ${property.area_total} m²` : `${property.area_total} m²`}
              </PropertyFeature>
              <PropertyFeature icon={BedDouble}>{property.habitaciones} Dormitorios</PropertyFeature>
              {isProject ? (
                <PropertyFeature icon={CalendarDays}>
                  {property.total_unidades ? `${property.total_unidades} unidades` : 'Consultar'}
                </PropertyFeature>
              ) : (
                <PropertyFeature icon={Bath}>1-2 Baños</PropertyFeature>
              )}
            </div>
          </div>

          {/* Footer con precio */}
          <div className="mt-4 pt-3 border-t border-gray-100
                        transform transition-all duration-300 group-hover:translate-x-1">
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-gray-500">Desde</span>
              <span className="text-xl font-bold text-orange-600">
                {formatPrice(property.precio_soles)}
              </span>
            </div>
          </div>

          {/* Decorative gradient overlay */}
          <div className={`absolute inset-0 pointer-events-none
                        bg-gradient-to-r from-orange-50/10 via-transparent to-amber-50/10
                        transition-opacity duration-500
                        ${isHovered ? 'opacity-100' : 'opacity-0'}`} 
          />
        </div>
      </div>
    </Card>
  );
};

export default PropertyCardPro;